import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { IForm } from 'core-components/classes/models/IForm'
import { sanitize } from 'core-components/helpers/sanitize'
import { BaseCrud, IDataProviderFilter, IQueryResult, Static } from 'core-components/types'

export const useBaseSource = <T = IForm, R = string | number>({
  source,
  caption,
  emptyValueLabel,
  maxListPageSize,
  fetchFilter,
  onDataLoad,
}: {
  source: BaseCrud<T, R>
  caption: string | ((item: T) => string)
  emptyValueLabel?: { value: string; dropDownKey?: string }
  maxListPageSize?: number
  fetchFilter?: IDataProviderFilter
  onDataLoad?: (items: T[]) => void
}) => {
  const [data, setData] = useState<T[]>([])
  const [initialData, setInitialData] = useState<T[]>([])
  const [isDataLoading, setIsDataLoading] = useState(true)
  const aliveRef = useRef(true)
  const isInitialized = useRef(false)

  useEffect(() => {
    return () => {
      aliveRef.current = false
    }
  }, [])

  const handleFetch = useCallback(
    (localData: IQueryResult<T, R>) => {
      if (aliveRef.current) {
        setData(() => {
          const { items } = localData
          const dataWithCaption = items.filter((el: T) => {
            if (typeof caption === 'string') {
              return el[caption]
            } else {
              return caption(el)
            }
          })

          if (items.length && !dataWithCaption.length) {
            console.error(
              sanitize(`Dropdown: Probably wrong caption. caption: '${caption}', items:`),
              sanitize(items)
            )
          }

          if (emptyValueLabel && typeof caption === 'string') {
            return [
              {
                DropdownKey: emptyValueLabel.dropDownKey || '',
                [caption]: emptyValueLabel.value,
              } as T,
              ...dataWithCaption,
            ]
          }
          if (!isInitialized.current) {
            setInitialData(dataWithCaption)
            isInitialized.current = true
          }

          return dataWithCaption
        })
      }
    },
    [caption, emptyValueLabel]
  )

  const fetchData = useCallback(() => {
    setIsDataLoading(true)
    source
      .fetch({ filter: fetchFilter })
      .then((localData) => {
        handleFetch(localData)
      })
      .finally(() => setIsDataLoading(false))
  }, [handleFetch, source, fetchFilter])

  useEffect(() => {
    fetchData()
    return source.subscribe('change', () => {
      fetchData()
    })
  }, [fetchData, source])

  useEffect(() => {
    if (onDataLoad && initialData.length) {
      onDataLoad(initialData)
    }
  }, [initialData, onDataLoad])

  const staticSource = useMemo(
    () =>
      new BaseCrud<T, R>({
        fetcher: new Static<T, R>(data),
        keyField: source.keyField,
        hierarchyField: source.hierarchyField,
        hierarchyRoot: source.hierarchyRoot,
        defaultPaging: {
          page: 0,
          pageSize: maxListPageSize ? maxListPageSize : data.length,
        },
      }),
    [data, maxListPageSize, source.hierarchyField, source.hierarchyRoot, source.keyField]
  )

  return { staticSource, data, isDataLoading, initialData }
}
